import React, { useEffect, useState } from 'react';
import axios from '../api/axios'; // ✅ uses your configured baseURL and headers
import styles from './Home.module.css';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { departments } from '../config/departments';
import RewardsActivityFeed from '../components/RewardsActivityFeed';
import TopContributorsLeaderboard from '../components/TopContributorsLeaderboard';
import ChatWidget from '../components/ChatWidget';
import { 
  Target, 
  Award, 
  Briefcase, 
  Heart, 
  Calendar, 
  Users, 
  Building2, 
  Info,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Unlock,
  BookOpen,
  AlertCircle,
  Plane,
  ArrowRight
} from 'lucide-react';

interface Post {
  id: number;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  postImageUrl?: string;
  videoUrl?: string;
  isVideoPost?: boolean;
}

interface Event {
  id: number;
  title: string;
  start: string;
  end: string;
  description: string;
  eventImageUrl?: string;
  isLeadershipOnly?: boolean;
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const [pinnedPosts, setPinnedPosts] = useState<Post[]>([]);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [stats, setStats] = useState({ totalMembers: 0, activeMissions: 0, upcomingEvents: 0 });
  const [showNewSiteBanner, setShowNewSiteBanner] = useState(() => {
    const viewCount = parseInt(localStorage.getItem('newSiteBannerViewCount') || '0', 10);
    const dismissed = localStorage.getItem('newSiteBannerDismissed') === 'true';
    return viewCount < 10 && !dismissed;
  });
  
  // Collapsible section states
  const [showBenefits, setShowBenefits] = useState(false);
  const [showQuickLinks, setShowQuickLinks] = useState(false);
  const [showResources, setShowResources] = useState(false);
  
  useEffect(() => {
    if (showNewSiteBanner) {
      const currentCount = parseInt(localStorage.getItem('newSiteBannerViewCount') || '0', 10);
      localStorage.setItem('newSiteBannerViewCount', (currentCount + 1).toString());
    }
  }, [showNewSiteBanner]);
  
  const dismissBanner = () => {
    localStorage.setItem('newSiteBannerDismissed', 'true');
    setShowNewSiteBanner(false);
  };  
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch posts
        const postsResponse = await axios.get('posts');
        console.log("🧪 /posts response:", postsResponse.data);
        const allPosts = postsResponse.data;

        setPinnedPosts(allPosts.filter((p: Post) => p.isPinned));
        setRecentPosts(allPosts.filter((p: Post) => !p.isPinned).slice(0, 3)); // Get 3 most recent

        // Fetch upcoming events (only for authenticated org members)
        if (user?.flags?.includes('OrgMember')) {
          const eventsResponse = await axios.get('events');
          const now = new Date();
          const upcoming = eventsResponse.data
            .filter((e: Event) => new Date(e.start) > now)
            .sort((a: Event, b: Event) => new Date(a.start).getTime() - new Date(b.start).getTime())
            .slice(0, 3); // Get 3 upcoming events
          setUpcomingEvents(upcoming);
        }

        // Fetch stats - only full members get missions/events data
        if (user?.flags?.includes('OrgMember')) {
          try {
            const [membersRes, missionsRes, eventsRes] = await Promise.all([
              axios.get('/auth/member-count'),
              axios.get('/missions/all'),
              axios.get('/events')
            ]);
            
            const now = new Date();
            const sevenDaysFromNow = new Date();
            sevenDaysFromNow.setDate(now.getDate() + 7);
            
            const activeMissions = missionsRes.data.filter((m: any) => 
              m.status === 'Open' || m.status === 'InProgress'
            ).length;
            const upcomingEventsCount = eventsRes.data.filter((e: any) => {
              const eventStart = new Date(e.start);
              return eventStart > now && eventStart <= sevenDaysFromNow;
            }).length;

            setStats({
              totalMembers: membersRes.data || 0,
              activeMissions,
              upcomingEvents: upcomingEventsCount
            });
          } catch (err) {
            console.error('Error fetching stats:', err);
          }
        } else {
          // For non-members (prospects & visitors), fetch public stats
          try {
            const [membersRes, missionsRes, eventsRes] = await Promise.all([
              axios.get('/auth/member-count'),
              axios.get('/missions/active-count'),
              axios.get('/events/upcoming-count')
            ]);
            
            setStats({
              totalMembers: membersRes.data || 0,
              activeMissions: missionsRes.data || 0,
              upcomingEvents: eventsRes.data || 0
            });
          } catch (err) {
            console.error('Error fetching public stats:', err);
            // Fallback to default values
            setStats({
              totalMembers: 100,
              activeMissions: 0,
              upcomingEvents: 0
            });
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    };

    fetchData();
  }, [user]);

  const openPostModal = (post: Post) => setSelectedPost(post);
  const closePostModal = () => setSelectedPost(null);

  const renderMediaPreview = (post: Post) => {
    if (post.isVideoPost && post.videoUrl) {
      return (
        <div className={styles.videoWrapper}>
          <iframe
            src={post.videoUrl.replace('watch?v=', 'embed/')}
            title="Video Post"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            width="100%"
            height="315" // <-- increased height
          />
        </div>
      );
    } else if (post.postImageUrl) {
      return (
        <img src={post.postImageUrl} alt="Post Image" className={styles.postImage} />
      );
    }
    return null;
  };
  

  return (
    <div className={styles.dashboardContainer}>
      {showNewSiteBanner && (
        <div className={styles.bannerMessage}>
          <AlertCircle className={styles.bannerIcon} />
          <p>
            Welcome to the redesigned TFIC platform. Previous user accounts were not migrated to this new system. 
            Please <Link to="/register">create a new account</Link> to get started. 
            We now maintain regular data backups to ensure your information is secure.
          </p>
          <button onClick={dismissBanner} className={styles.dismissButton}>×</button>
        </div>
      )}

      {/* Compact Welcome Header */}
      <div className={styles.compactWelcome}>
        <div className={styles.welcomeText}>
          <h1>Welcome to TFIC</h1>
          <p>
            {user 
              ? `Welcome back, ${user.username || user.unique_name}!`
              : 'Your gateway to organized Star Citizen operations.'}
          </p>
        </div>
        {!user && (
          <div className={styles.welcomeCTA}>
            <Link to="/register" className={styles.primaryCTA}>Join TFIC</Link>
            <Link to="/aboutus" className={styles.secondaryCTA}>Learn More</Link>
          </div>
        )}
        {user && !user.flags?.includes('OrgMember') && (
          <Link to="/dashboard" className={styles.onboardingPrompt}>
            🎯 Complete Onboarding →
          </Link>
        )}
      </div>

      {/* Call to Action Section for Non-Members - MOVED TO TOP */}
      {!user ? (
        // Non-Authenticated Visitor - Primary CTA at the top
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2>Ready to Start Your Journey?</h2>
            <p>Join TFIC today and become part of a professional, organized Star Citizen corporation with {stats.totalMembers}+ active members</p>
            <div className={styles.ctaButtons}>
              <Link to="/register" className={styles.ctaPrimary}>Create Account</Link>
              <Link to="/aboutus" className={styles.ctaSecondary}>Learn More About Us</Link>
            </div>
            <div className={styles.ctaStats}>
              <div className={styles.ctaStat}>
                <strong>{stats.totalMembers}</strong>
                <span>Active Members</span>
              </div>
              <div className={styles.ctaStat}>
                <strong>{stats.activeMissions}</strong>
                <span>Open Missions</span>
              </div>
              <div className={styles.ctaStat}>
                <strong>8</strong>
                <span>Departments</span>
              </div>
            </div>
          </div>
        </div>
      ) : user && !user.flags?.includes('OrgMember') ? (
        // Registered Prospect - Guide to Orientation (TOP PRIORITY)
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2><Target size={32} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', strokeWidth: 2.5}} /> Complete Your Orientation</h2>
            <p>Welcome! You're just 3 steps away from full member access. Complete the checklist below to unlock all TFIC features and benefits.</p>
            <div className={styles.ctaButtons}>
              <Link to="/dashboard" className={styles.ctaPrimary}>Go to Dashboard</Link>
              <a href="https://discord.gg/tfic" target="_blank" rel="noopener noreferrer" className={styles.ctaSecondary}>Open Discord</a>
            </div>
            <div className={styles.orientationSteps}>
              <div className={styles.orientationStep}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <h4>Read Code of Conduct</h4>
                  <p>Review and accept our community guidelines in your Dashboard</p>
                </div>
              </div>
              <div className={styles.orientationStep}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <h4>Verify Discord Account</h4>
                  <p>Complete Discord verification in our server's verification channel</p>
                </div>
              </div>
              <div className={styles.orientationStep}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepContent}>
                  <h4>Join TFIC on RSI Website</h4>
                  <p>Officially join the MASTERLS organization on robertsspaceindustries.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Ship Database Promo - Shown to everyone */}
      <div className={styles.shipDatabasePromo}>
        <div className={styles.promoContent}>
          <div className={styles.promoIcon}>
            <Plane size={48} />
          </div>
          <div className={styles.promoText}>
            <h3>Star Citizen Ship Database</h3>
            <p>
              Explore our comprehensive ship database featuring detailed specs, loadouts, 
              and comparisons for all Star Citizen vehicles. Free and open to everyone.
            </p>
            <div className={styles.promoFeatures}>
              <span>✓ 200+ Ships</span>
              <span>✓ Detailed Specs</span>
              <span>✓ Component Loadouts</span>
              <span>✓ No Account Required</span>
            </div>
          </div>
          <Link to="/ship-database" className={styles.promoCta}>
            Browse Ships <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      {/* Org Showcase for Non-Authenticated Visitors */}
      {!user && (
        <div className={styles.orgShowcase}>
          <h2 className={styles.sectionTitle}>What Makes TFIC Different?</h2>
          <div className={styles.showcaseGrid}>
            <div className={styles.showcaseCard}>
              <div className={styles.showcaseNumber}>8</div>
              <h3>Specialized Departments</h3>
              <p>From Security and Logistics to Medical and Intelligence, find your role in a structured organization</p>
            </div>
            <div className={styles.showcaseCard}>
              <div className={styles.showcaseNumber}><Heart size={56} /></div>
              <h3>Emergency Response</h3>
              <p>Dedicated medical rescue team ready to respond to in-game emergencies when available</p>
            </div>
            <div className={styles.showcaseCard}>
              <div className={styles.showcaseNumber}>{stats.totalMembers || '100'}+</div>
              <h3>Active Members</h3>
              <p>Join a thriving community of pilots ready to coordinate and accomplish missions together</p>
            </div>
            <div className={styles.showcaseCard}>
              <div className={styles.showcaseNumber}>∞</div>
              <h3>Career Progression</h3>
              <p>Unlimited growth potential through our comprehensive leveling and position advancement system</p>
            </div>
          </div>
        </div>
      )}

      {/* What You'll Unlock Section - For Prospects */}
      {user && !user.flags?.includes('OrgMember') && (
        <div className={styles.benefitsSection}>
          <h2 className={styles.sectionTitle}><Unlock size={32} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '8px', strokeWidth: 2.5}} /> What You'll Unlock After Orientation</h2>
          <p className={styles.sectionSubtitle}>
            Complete your orientation to access these exclusive member features
          </p>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Target size={48} /></div>
              <h3>Mission System</h3>
              <p>Accept missions, complete objectives, and earn org points. Level up to unlock new opportunities and responsibilities within the organization.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Award size={48} /></div>
              <h3>Rewards & Progression</h3>
              <p>Earn badges, climb levels, and unlock exclusive features. Your contributions are recognized and rewarded through our comprehensive tracking system.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Calendar size={48} /></div>
              <h3>Events & Operations</h3>
              <p>Browse and RSVP for organized events, training sessions, and operations. Coordinate with your team and participate in scheduled activities.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Briefcase size={48} /></div>
              <h3>Position Applications</h3>
              <p>Apply for specialized roles across 8 departments. Advance your career path and take on leadership positions as you grow with the organization.</p>
            </div>
          </div>
        </div>
      )}

      {/* Member Benefits Section - Show More Details for Authenticated Members */}
      {user?.flags?.includes('OrgMember') ? (
        <div className={styles.benefitsSection}>
          <div className={styles.collapsibleHeader} onClick={() => setShowBenefits(!showBenefits)}>
            <div className={styles.headerContent}>
              <h2 className={styles.sectionTitle}>Your Member Benefits</h2>
              <span className={styles.expandHint}>{showBenefits ? 'Click to collapse' : 'Click to expand'}</span>
            </div>
            {showBenefits ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
          </div>
          {showBenefits && (
            <div className={styles.benefitsGrid}>
              <div className={styles.benefitCard}>
                <div className={styles.benefitIcon}><Target size={48} /></div>
                <h3>Mission System</h3>
                <p>Accept missions, complete objectives, and earn org points. Level up to unlock new opportunities and responsibilities.</p>
                <Link to="/missions" className={styles.benefitLink}>Browse Missions →</Link>
              </div>
              <div className={styles.benefitCard}>
                <div className={styles.benefitIcon}><Award size={48} /></div>
                <h3>Progression & Rewards</h3>
                <p>Earn badges, climb levels, and unlock exclusive features. Your contributions are recognized and rewarded.</p>
                <Link to="/dashboard" className={styles.benefitLink}>View Progress →</Link>
              </div>
              <div className={styles.benefitCard}>
                <div className={styles.benefitIcon}><Briefcase size={48} /></div>
                <h3>Position Applications</h3>
                <p>Apply for specialized roles in departments. Advance your career and take on leadership positions.</p>
                <Link to="/departments" className={styles.benefitLink}>Explore Positions →</Link>
              </div>
              <div className={styles.benefitCard}>
                <div className={styles.benefitIcon}><Heart size={48} /></div>
                <h3>Emergency Services</h3>
                <p>Request medical assistance in-game with our dedicated rescue team. Fast response, professional support.</p>
                <Link to="/medical-sos" className={styles.benefitLink}>Request SOS →</Link>
              </div>
              <div className={styles.benefitCard}>
                <div className={styles.benefitIcon}><Users size={48} /></div>
                <h3>Active Community</h3>
                <p>Connect with {stats.totalMembers}+ members through Discord, in-game operations, and our web platform. Build lasting friendships and reliable squadmates.</p>
              </div>
            </div>
          )}
        </div>
      ) : !user && (
        <div className={styles.benefitsSection}>
          <h2 className={styles.sectionTitle}>Why Join TFIC?</h2>
          <p className={styles.sectionSubtitle}>
            Experience organized gameplay, career progression, and a supportive community in Star Citizen
          </p>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Target size={48} /></div>
              <h3>Structured Missions</h3>
              <p>Accept and complete missions tailored to your skills. Earn org points, unlock rewards, and contribute to collective goals with clear objectives and recognition.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Award size={48} /></div>
              <h3>Career Progression</h3>
              <p>Rise through the ranks with our comprehensive leveling system. Earn badges, unlock exclusive features, and advance into specialized leadership roles across departments.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Building2 size={48} /></div>
              <h3>Department Structure</h3>
              <p>Join specialized departments: Security, Logistics, Medical, Intelligence, Technology, and more. Find your niche and work with like-minded pilots.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Heart size={48} /></div>
              <h3>Emergency Support</h3>
              <p>Access our dedicated medical rescue team 24/7. Request assistance during emergencies and experience professional coordination when you need it most.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Calendar size={48} /></div>
              <h3>Organized Events</h3>
              <p>Participate in scheduled operations, training sessions, and community events. RSVP with role selection and coordinate with your team for maximum effectiveness.</p>
            </div>
            <div className={styles.benefitCard}>
              <div className={styles.benefitIcon}><Users size={48} /></div>
              <h3>Active Community</h3>
              <p>Connect with {stats.totalMembers}+ members through Discord, in-game operations, and our web platform. Build lasting friendships and reliable squadmates.</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area with Sidebar Layout */}
      <div className={styles.mainContentWrapper}>
        {/* Left: Primary Content - Collapsible Sections */}
        <div className={styles.primaryContent}>
          {/* Quick Links Dashboard */}
          <div className={styles.quickLinksSection}>
        <div className={styles.collapsibleHeader} onClick={() => setShowQuickLinks(!showQuickLinks)}>
          <div className={styles.headerContent}>
            <h2 className={styles.sectionTitle}>Quick Access</h2>
            <span className={styles.expandHint}>{showQuickLinks ? 'Click to collapse' : 'Click to expand'}</span>
          </div>
          {showQuickLinks ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
        </div>
        {showQuickLinks && (
          <div className={styles.quickLinksGrid}>
            {user?.flags?.includes('OrgMember') && (
              <>
                <Link to="/missions" className={styles.quickLinkCard}>
                  <div className={styles.quickLinkIcon}>
                    <Target size={48} />
                  </div>
                  <h3>Missions</h3>
                  <p>Accept missions and earn org points</p>
                </Link>

                <Link to="/events" className={styles.quickLinkCard}>
                  <div className={styles.quickLinkIcon}>
                    <Calendar size={48} />
                  </div>
                  <h3>Events Calendar</h3>
                  <p>View and RSVP to upcoming events</p>
                </Link>
              </>
            )}

            {user && (
              <>
                <Link to="/knowledge" className={styles.quickLinkCard}>
                  <div className={styles.quickLinkIcon}>
                    <BookOpen size={48} />
                  </div>
                  <h3>Knowledge Base</h3>
                  <p>Browse guides, tutorials, and training materials</p>
                </Link>

                <Link to="/departments" className={styles.quickLinkCard}>
                  <div className={styles.quickLinkIcon}>
                    <Building2 size={48} />
                  </div>
                  <h3>Departments</h3>
                  <p>Explore our departments and their operations</p>
                </Link>

                <Link to="/medical-sos" className={styles.quickLinkCard}>
                  <div className={styles.quickLinkIcon}>
                    <Heart size={48} />
                  </div>
                  <h3>Medical SOS</h3>
                  <p>Request emergency medical assistance in-game</p>
                </Link>
              </>
            )}

            {user ? (
              <Link to="/dashboard" className={styles.quickLinkCard}>
                <div className={styles.quickLinkIcon}>
                  <Users size={48} />
                </div>
                <h3>My Profile</h3>
                <p>View and manage your profile settings</p>
              </Link>
            ) : (
              <Link to="/register" className={styles.quickLinkCard}>
                <div className={styles.quickLinkIcon}>
                  <Users size={48} />
                </div>
                <h3>Join Us</h3>
                <p>Register to become a member</p>
              </Link>
            )}

            <Link to="/aboutus" className={styles.quickLinkCard}>
              <div className={styles.quickLinkIcon}>
                <Info size={48} />
              </div>
              <h3>About Us</h3>
              <p>Learn more about TFIC and our mission</p>
            </Link>

            <Link to="/changelog" className={styles.quickLinkCard}>
              <div className={styles.quickLinkIcon}>
                <FileText size={48} />
              </div>
              <h3>What's New</h3>
              <p>See the latest updates and features</p>
            </Link>
          </div>
        )}
      </div>

      {/* Events & News Section */}
      {user?.flags?.includes('OrgMember') && (
        <div className={styles.eventsNewsSection}>
          <div className={styles.eventsNewsGrid}>
            {/* Upcoming Events */}
            <div className={styles.dashboardSection}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Upcoming Events</h2>
                <Link to="/events" className={styles.viewAllLink}>View All →</Link>
              </div>
              {upcomingEvents.length === 0 ? (
                <p className={styles.emptyState}>No upcoming events scheduled</p>
              ) : (
                <div className={styles.eventsList}>
                  {upcomingEvents.map(event => (
                    <Link 
                      key={event.id} 
                      to={`/events/${event.id}`} 
                      className={styles.eventCard}
                    >
                      {event.eventImageUrl && (
                        <img src={event.eventImageUrl} alt={event.title} className={styles.eventImage} />
                      )}
                      <div className={styles.eventDetails}>
                        <h3>{event.title}</h3>
                        {event.isLeadershipOnly && (
                          <span className={styles.leadershipBadge}>🔒 Leadership</span>
                        )}
                        <p className={styles.eventDate}>
                          📅 {new Date(event.start).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Latest News/Posts */}
            <div className={styles.dashboardSection}>
          {/* Pinned Posts */}
          {pinnedPosts.length > 0 && (
            <>
              <h2 className={styles.sectionTitle}>📌 Pinned Announcements</h2>
              <div className={styles.pinnedPostsList}>
                {pinnedPosts.map(post => (
                  <div key={post.id} className={styles.pinnedPostCard} onClick={() => openPostModal(post)}>
                    <h3>{post.title}</h3>
                    <p className={styles.timestamp}>
                      {new Date(post.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Recent Posts */}
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Latest News</h2>
            <Link to="/posts" className={styles.viewAllLink}>View All →</Link>
          </div>
          {recentPosts.length === 0 ? (
            <p className={styles.emptyState}>No recent posts</p>
          ) : (
            <div className={styles.recentPostsList}>
              {recentPosts.map(post => (
                <div key={post.id} className={styles.postCard} onClick={() => openPostModal(post)}>
                  {renderMediaPreview(post)}
                  <h3>{post.title}</h3>
                  <p className={styles.timestamp}>
                    {new Date(post.createdAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
          </div>
        </div>
      )}

      {/* Resources Guide Section */}
      <div className={styles.resourcesSection}>
        <div className={styles.collapsibleHeader} onClick={() => setShowResources(!showResources)}>
          <div className={styles.headerContent}>
            <h2 className={styles.sectionTitle}>Website Resources</h2>
            <span className={styles.expandHint}>{showResources ? 'Click to collapse' : 'Click to expand'}</span>
          </div>
          {showResources ? <ChevronUp size={28} /> : <ChevronDown size={28} />}
        </div>
        {showResources && (
          <div className={styles.resourcesGrid}>
            <div className={styles.resourceCard}>
              <div className={styles.resourceIcon}>
                <Calendar size={40} />
              </div>
              <h3>Events & RSVP</h3>
              <p>Browse upcoming events, RSVP for operations, and manage your attendance. Leadership can create and manage events.</p>
            </div>
            <div className={styles.resourceCard}>
              <div className={styles.resourceIcon}>
                <Heart size={40} />
              </div>
              <h3>Medical Services</h3>
              <p>Submit emergency requests for in-game medical assistance. Our medical team will respond and coordinate rescue operations.</p>
            </div>
            <div className={styles.resourceCard}>
              <div className={styles.resourceIcon}>
                <Building2 size={40} />
              </div>
              <h3>Departments</h3>
              <p>Explore our organization structure. Each department has videos, resources, and application systems for members.</p>
            </div>
            <div className={styles.resourceCard}>
              <div className={styles.resourceIcon}>
                <Users size={40} />
              </div>
              <h3>Member Directory</h3>
              <p>Connect with other members, view position assignments, and build relationships within the organization.</p>
            </div>
          </div>
        )}
      </div>

      {/* Compact Stats Footer */}
      {user?.flags?.includes('OrgMember') && (
        <div className={styles.statsFooter}>
          <div className={styles.statsFooterGrid}>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{stats.totalMembers}</div>
              <div className={styles.statLabel}>Active Members</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{stats.activeMissions}</div>
              <div className={styles.statLabel}>Open Missions</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{stats.upcomingEvents}</div>
              <div className={styles.statLabel}>Upcoming Events</div>
              <div className={styles.statNote}>Next 7 days</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>{departments.length}</div>
              <div className={styles.statLabel}>Departments</div>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Right: Sidebar - Rewards & Leaderboard (Members & Prospects) */}
      {user && (
        <div className={styles.sidebarContent}>
          <div className={styles.sidebarSection}>
            <ChatWidget currentUserId={user?.id} />
          </div>
          <div className={styles.sidebarSection}>
            <RewardsActivityFeed limit={8} showTitle={true} />
          </div>
          <div className={styles.sidebarSection}>
            <TopContributorsLeaderboard timeframe="week" limit={5} showTitle={true} />
          </div>
        </div>
      )}
      </div>

      {/* Post Modal */}
      {selectedPost && (
        <div className={styles.modalOverlay} onClick={closePostModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={closePostModal}>&times;</button>
            <h2>{selectedPost.title}</h2>

            {selectedPost.isVideoPost && selectedPost.videoUrl ? (
              <div className={styles.videoContainer}>
                <iframe
                  width="100%"
                  height="400"
                  src={selectedPost.videoUrl.replace("watch?v=", "embed/")}
                  title="Video Post"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : selectedPost.postImageUrl ? (
              <img
                src={selectedPost.postImageUrl}
                alt="Full Post"
                className={styles.modalImage}
              />
            ) : null}

            <div
              className={styles.postContent}
              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
            />

            <p className={styles.timestamp}>
              {new Date(selectedPost.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;
